import EF from 'ef/ef';
import Chaplin from 'chaplin';
import ResponseSet from 'ef/models/evaluations/response_set';
import utils from 'lib/utils';
import ErrorHandler from 'services/error-handler-service/handler';
import { HTTPError } from 'services/error-handler-service/error';

const EvaluationService = {
  initialize: function (evaluationMap) {
    evaluationMap = evaluationMap || {};
    this.mapping = evaluationMap;
  },

  getResponseSet: function (courseSlug) {
    const fakeResponseSet = {
      id: 'local',
      token: 'not-real',
      endpoint: 'http://nowhere.dev',
    };
    if (this.mapping && this.mapping[courseSlug]) {
      return this.mapping[courseSlug];
    } else {
      return fakeResponseSet;
    }
  },

  userHasPassedEvaluation: async function (courseSlug) {
    const responseSet = await this.findEvaluationBySlug(courseSlug);
    if (responseSet) {
      return responseSet.get('grading_status') === 'passed';
    }
  },

  userHasCompletedEvaluation: async function (courseSlug) {
    const responseSet = await this.findEvaluationBySlug(courseSlug);
    if (responseSet) {
      const completed =
        responseSet.get('has_been_completed') ||
        responseSet.get('status') === 'completed' ||
        this.mapping[courseSlug].status === 'skipped';
      if (completed) {
        this.refreshResponseSet(courseSlug);
      }
      return completed;
    }
  },

  shouldSkip: function (slug) {
    const responseSet = this.getResponseSet(slug);
    return responseSet && responseSet.status && responseSet.status === 'skipped';
  },

  getResponseSetFromModel: async function (courseSlug) {
    const evaluation = await this.findEvaluationBySlug(courseSlug);
    const responseSet = evaluation ? evaluation.get('responseSet') : null;

    if (responseSet) {
      responseSet.endpoint = evaluation.get('endpoint');
      responseSet.id = evaluation.get('id');
    }
    return responseSet;
  },

  findEvaluationBySlug: async function (slug) {
    const evalufiId = this.mapping[slug] && !this.isResponseSetScrubbed(slug) ? this.mapping[slug].id : null;

    try {
      return evalufiId ? await this.getEvaluationModel(evalufiId) : await this.handleMissingOrRetakeResponseSet(slug);
    } catch (error) {
      // Something went wrong
      ErrorHandler.handle(error, 'warn');
      return null;
    }
  },
  isResponseSetScrubbed: function (slug) {
    return !this.mapping[slug].evalufi_id;
  },
  getEvaluationModel: async function (evalufiId) {
    return EF.responseSet.models.find(model => {
      return model.get('id') === evalufiId;
    });
  },
  handleMissingOrRetakeResponseSet: async function (courseSlug, isRetake) {
    const progressId = EF.enrollment.get('id');
    const basePath = EF.config.get('basePath');
    const retakePath = isRetake ? `/${courseSlug}/retake` : '';
    const endPoint = `${basePath}/progress/${progressId}/response_sets${retakePath}`;

    const timeoutInterval = 1000;
    const GET = 'GET';
    const POST = 'POST';
    const PUT = 'PUT';
    let tries = 3;
    let foundResponseSet;

    const fetchResponseSet = async (url, method, slug) => {
      try {
        const getParams = httpMethod => {
          const body =
            httpMethod === POST
              ? {
                  body: JSON.stringify({
                    data: {
                      type: 'response_sets',
                      attributes: {
                        course_evaluation_slug: slug,
                      },
                    },
                  }),
                }
              : {};

          return {
            method: httpMethod,
            headers: {
              Accept: 'application/vnd.api+json',
              'Content-Type': 'application/vnd.api+json',
              Cache: 'no-cache',
              Authorization: `Bearer ${EF.config.get('accessToken')}`,
            },
            ...body,
          };
        };
        const response = await fetch(method === GET ? `${endPoint}/${slug}` : url, getParams(method));
        tries--;
        if (tries === 0) {
          throw new HTTPError('Re-tried and failed to get response set ', response.status);
        }

        // If the responseSet does not exist, we need to generate it synchronously
        if (response.status === 404 || response.status >= 500) {
          setTimeout(await fetchResponseSet(url, isRetake ? PUT : POST, slug), timeoutInterval);
        } else if (response.status === 409) {
          /**
           * The endpoint attempts to acquire the lock without waiting.
           * If the lock cannot be acquired, then a 409 error will be returned.
           * This will be a signal that the response sets are already being created
           * and it can thus wait and go into a polling state to see when they are created which
           * should be within a few seconds tops.
           */
          setTimeout(await fetchResponseSet(url, isRetake ? PUT : GET, slug), timeoutInterval);
        } else if (response.status >= 200 && response.status < 300) {
          /**
           * Due to possible race conditions with the backend job,
           * this endpoint must first examine the progress record to see if the response set already exists.
           * If not, then it should create it and return the response set with a 201 status code.
           * If the response set already exists it should simply return it with a 200 status code.
           */
          const results = await response.json();
          if (results.data.id) {
            const responseSet = utils.sanifyJsonApiObject(results.data);
            responseSet.id = responseSet.evalufi_id;
            this.mapping[slug] = responseSet;
            foundResponseSet = new ResponseSet(responseSet);
            EF.responseSet.add(foundResponseSet);
          }
        } else {
          throw new HTTPError('Failed to get response set for slug ', response.status);
        }
      } catch (error) {
        ErrorHandler.handle(error);
        return null;
      }
    };

    await fetchResponseSet(endPoint, isRetake ? PUT : GET, courseSlug);
    return foundResponseSet;
  },

  findEvaluationTokenBySlug: function (slug) {
    return this.mapping[slug].token;
  },

  expandedResponseSet: async function (slug) {
    const responseSet = await this.findEvaluationBySlug(slug);
    if (!responseSet) {
      // responseSet does not exist / maybe regenerate it
      return null;
    }
    const options = this.getResponseSetOptions(responseSet);
    responseSet.includes = 'evaluation,sections.questions.answers,classifications';
    await responseSet.fetch(options);
  },

  updateResponseSet: async function (slug, newResponseSet) {
    const responseSet = await this.findEvaluationBySlug(slug);
    if (responseSet && responseSet.id !== newResponseSet.id) {
      this.mapping[slug] = newResponseSet;
      responseSet.id = newResponseSet.id;
      responseSet.token = newResponseSet.token;
      responseSet.set('id', newResponseSet.id);
      responseSet.set('token', newResponseSet.token);
    }
    this.refreshResponseSet(slug);
  },

  refreshResponseSet: async function (slug) {
    // findEvaluationBySlug called twice.
    const responseSet = await this.findEvaluationBySlug(slug);
    if (!responseSet) {
      return new Promise(resolve => resolve());
    }
    const options = this.getResponseSetOptions(responseSet);
    responseSet.includes = 'classifications';
    return responseSet.fetch(options);
  },
  getResponseSetOptions: function (responseSet) {
    return EF.options.local
      ? {}
      : {
          parse: true,
          headers: { Authorization: `Bearer ${responseSet.get('token')}` },
        };
  },

  bestScore: async function (courseSlug) {
    const responseSet = await this.findEvaluationBySlug(courseSlug);
    return responseSet && responseSet.has('best_grade') ? responseSet.get('best_grade') : 0;
  },

  showAlert: /* istanbul ignore next: moved to alert service */ alertConfig => {
    if (!alertConfig || alertConfig.length !== 5) {
      return;
    }

    const [alertType, alertTitle, icon, visibility, alertMsg] = alertConfig;
    Chaplin.mediator.publish('action:alert', alertType, alertTitle, alertMsg, icon, visibility);
  },
};

export default EvaluationService;
